import { Link } from 'wouter';
import { User } from 'firebase/auth';
import { Menu, X, Home, Info, Briefcase, Plus, ShoppingBag, Shield, User as UserIcon, LogOut, Mail, Instagram, Facebook, Tv, Crown, Brain, Store, Beef, GraduationCap } from 'lucide-react';
import { FaTwitter, FaTiktok } from 'react-icons/fa';
import { useState } from 'react';
import { logout } from '@/lib/firebase';

interface NavigationProps {
  user: User | null;
}

export default function Navigation({ user }: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSignIn = async () => {
    // Redirect to login page instead of direct Google sign-in
    window.location.href = '/login';
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <nav className="shadow-lg border-b-2 border-red-700 relative" style={{ backgroundColor: '#f8f6f3' }}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <img src="/logo.png" alt="BLK BOOK — blackbook.family" className="h-16 w-auto object-contain" />
          </Link>

          {/* Right Side - Social Media & Navigation */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Social Media Links - Responsive sizing */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <a 
                href="https://www.instagram.com/theeeblackbook?igsh=NjBkajJkcTlpNTdx&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-fba-red text-black p-1 sm:p-1.5 md:p-2 rounded-full hover:scale-110 transition-transform shadow-lg hover:bg-red-600"
                aria-label="Follow us on Instagram"
              >
                <Instagram size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61578640042420" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-fba-red text-black p-1 sm:p-1.5 md:p-2 rounded-full hover:scale-110 transition-transform shadow-lg hover:bg-red-600"
                aria-label="Follow us on Facebook"
              >
                <Facebook size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </a>
              <a 
                href="https://twitter.com/theeeblackbook" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-fba-red text-black p-1 sm:p-1.5 md:p-2 rounded-full hover:scale-110 transition-transform shadow-lg hover:bg-red-600"
                aria-label="Follow us on X"
              >
                <FaTwitter size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </a>
              <a 
                href="https://www.tiktok.com/@theeeblackbook" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-fba-red text-black p-1 sm:p-1.5 md:p-2 rounded-full hover:scale-110 transition-transform shadow-lg hover:bg-red-600"
                aria-label="Follow us on TikTok"
              >
                <FaTiktok size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </a>
            </div>
            
            {/* Desktop Navigation - Hidden on mobile and tablet */}
            <div className="hidden xl:flex items-center space-x-6">
              {/* ── BLK BOOK core ── */}
              <Link href="/" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Home size={18} />
                <span>Home</span>
              </Link>
              <Link href="/about" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Info size={18} />
                <span>About</span>
              </Link>
              <Link href="/hbcu" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <GraduationCap size={18} />
                <span>HBCUs</span>
              </Link>
              <Link href="/jobs" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Briefcase size={18} />
                <span>Jobs</span>
              </Link>
              <Link href="/merch" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <ShoppingBag size={18} />
                <span>Merch</span>
              </Link>
              <Link href="/add-business" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Plus size={18} />
                <span>Add Business</span>
              </Link>

              {/* ── Partner / external ── */}
              <span className="text-gray-300 select-none">|</span>
              <Link href="/bmz" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Tv size={18} />
                <span>BMZ</span>
              </Link>
              <Link href="/beauty" className="text-blue-900 hover:text-red-700 font-medium flex items-center space-x-1">
                <Crown size={18} />
                <span>Beauty of the Week</span>
              </Link>
            </div>
            
            {/* User Authentication - Desktop */}
            <div className="hidden xl:flex items-center space-x-4">
              {user ? (
                <div className="flex items-center space-x-3">
                  {user.photoURL && (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span className="text-blue-900 font-medium">{user.displayName}</span>
                  <button
                    onClick={handleSignOut}
                    className="text-blue-900 hover:text-red-700 flex items-center space-x-1"
                  >
                    <LogOut size={18} />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSignIn}
                  className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium flex items-center space-x-1"
                >
                  <UserIcon size={18} />
                  <span>Sign In</span>
                </button>
              )}
            </div>
            
            {/* Hamburger Menu Button - Mobile & Tablet */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-md hover:bg-gray-200"
            >
              {isMenuOpen ? <X size={24} className="text-blue-900" /> : <Menu size={24} className="text-blue-900" />}
            </button>
          </div>
          

        </div>

        {/* Mobile & Tablet Navigation */}
        {isMenuOpen && (
          <div className="xl:hidden pb-4 border-t border-red-200 mt-4 pt-4 bg-white shadow-lg rounded-lg absolute top-full left-4 right-4 z-50">
            <div className="flex flex-col space-y-2">
              {/* ── BLK BOOK core ── */}
              <Link 
                href="/" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Home size={24} />
              </Link>

              <Link 
                href="/about" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Info size={24} />
              </Link>

              <Link 
                href="/hbcu" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <div className="flex items-center space-x-2">
                  <GraduationCap size={24} />
                  <span className="text-sm font-semibold">HBCUs</span>
                </div>
              </Link>

              <Link 
                href="/jobs" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Briefcase size={24} />
              </Link>

              <Link 
                href="/merch" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <ShoppingBag size={24} />
              </Link>

              <Link 
                href="/add-business" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Plus size={24} />
              </Link>

              {/* Contact */}
              <a 
                href="mailto:info@blackbook.family" 
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Mail size={24} />
              </a>

              {/* ── Partner / external ── */}
              <div className="border-t border-gray-200 my-1" />

              <Link 
                href="/bmz" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Tv size={24} />
              </Link>

              <Link 
                href="/beauty" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
                data-testid="link-beauty-of-the-week"
              >
                <div className="flex items-center space-x-2">
                  <Crown size={24} />
                  <span className="text-sm font-semibold">Beauty of the Week</span>
                </div>
              </Link>

              <Link 
                href="/blkgpt" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Brain size={24} />
              </Link>

              <Link 
                href="/blkmart" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Store size={24} />
              </Link>

              <Link 
                href="/crowncattle" 
                onClick={() => setIsMenuOpen(false)}
                className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
              >
                <Beef size={24} />
              </Link>

              {user?.email === 'admin@blackbook.com' && (
                <Link 
                  href="/admin" 
                  onClick={() => setIsMenuOpen(false)}
                  className="text-blue-900 hover:text-red-700 font-medium px-3 py-2 flex items-center justify-center rounded-md hover:bg-red-50"
                >
                  <Shield size={24} />
                </Link>
              )}

              {/* Social Media Links */}
              <div className="border-t border-red-200 pt-4 mt-4 bg-red-50 rounded-lg">
                <p className="text-sm text-blue-900 text-center mb-3 font-semibold">📱 Follow Us</p>
                <div className="flex justify-center space-x-4">
                  <a 
                    href="https://www.instagram.com/theeeblackbook?igsh=NjBkajJkcTlpNTdx&utm_source=qr" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-3 rounded-full hover:scale-110 transition-transform shadow-lg"
                    aria-label="Follow us on Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a 
                    href="https://www.facebook.com/profile.php?id=61578640042420" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="bg-blue-600 text-white p-3 rounded-full hover:scale-110 transition-transform shadow-lg"
                    aria-label="Follow us on Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
                <p className="text-xs text-red-700 text-center mt-2">@theeeblackbook</p>
              </div>

              <div className="border-t border-red-200 pt-4 mt-4">
                {user ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 px-3">
                      {user.photoURL && (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName || 'User'} 
                          className="w-8 h-8 rounded-full"
                        />
                      )}
                      <span className="text-sm text-blue-900">{user.displayName}</span>
                    </div>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center justify-center"
                    >
                      <LogOut size={24} />
                    </button>
                  </div>
                ) : (
                  <Link
                    href="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="w-full flex items-center justify-center px-3 py-2 bg-red-700 hover:bg-red-800 text-white rounded-md transition-colors"
                  >
                    <UserIcon size={24} />
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}